import React from 'react';
import PropTypes from 'prop-types';
import {
  extractCities,
  extractCountries,
  filterByCountry as filterOfficesByCountry, getCityByOfficeId,
  getCountryByOfficeId,
} from 'redux/models/offices/model';
import {
  PageBlock,
  PageBlockHeader,
  PageBlockContent,
  PageBlockCaption,
  PageBlockSection,
  PageBlockSections
} from 'theme/components/Layout/Layout';
import { ParamsContext } from 'components/Context/ParamsContext';
import {
  PARAM_OFFICE_SELECTED,
  PARAM_INFO_BLOCKS,
  PARAM_INFO_BLOCKS_MODAL_TITLE,
  PARAM_INFO_BLOCKS_MODAL_INFO
} from 'config/params';
import OfficePageCountries from '../OfficePageCountries/OfficePageCountries';
import OfficePageCities from '../OfficePageCities/OfficePageCities';
import OfficePageNote from 'theme/components/Note/Note';
import OfficePageList from '../OfficePageList/OfficePageList';
import first from 'lodash/first';
import isEmpty from 'lodash/isEmpty';

const OfficePageContent = ({ offices }) => {
  const {
    [PARAM_OFFICE_SELECTED]: selectedOffice,
    [PARAM_INFO_BLOCKS]: {
      [PARAM_INFO_BLOCKS_MODAL_TITLE]: modalTitle,
      [PARAM_INFO_BLOCKS_MODAL_INFO]: modalInfo,
    }
  } = React.useContext(ParamsContext);

  const countries = React.useMemo(() => extractCountries(offices, 'Россия'), [offices]);
  const activeCountryInitial = React.useMemo(() => {
    if (!selectedOffice) {
      return first(countries);
    }
    return getCountryByOfficeId(offices, selectedOffice) || first(countries);
  }, [offices, selectedOffice, countries]);

  const [activeCountry, setActiveCountry] = React.useState(activeCountryInitial);
  const officesByActiveCountry = React.useMemo(() => {
    if (!activeCountry) {
      return offices;
    }
    const filteredOffices = filterOfficesByCountry(offices, activeCountry);
    return !isEmpty(filteredOffices) ? filteredOffices : offices;
  }, [offices, activeCountry]);

  const cities = React.useMemo(() => extractCities(officesByActiveCountry), [officesByActiveCountry]);
  const activeCityInitial = React.useMemo(() => {
    if (!selectedOffice) {
      return first(cities);
    }
    return getCityByOfficeId(officesByActiveCountry, selectedOffice) || first(cities);
  }, [officesByActiveCountry, selectedOffice, cities]);
  const [activeCity, setActiveCity] = React.useState(activeCityInitial);

  const firstRender = React.useRef(true);
  React.useEffect(() => {
    if (!firstRender.current) {
      setActiveCity(first(cities));
    }
    firstRender.current = false;
  }, [cities, setActiveCity]);

  return (
    <PageBlock>
      <PageBlockHeader isSeparated>
        <PageBlockCaption>
          {modalTitle}
        </PageBlockCaption>
        {!isEmpty(countries) && (
          <OfficePageCountries
            countries={countries}
            activeCountry={activeCountry}
            setActiveCountry={setActiveCountry}
          />
        )}
      </PageBlockHeader>
      <PageBlockContent>
        <PageBlockSections>
          <PageBlockSection col2>
            <OfficePageCities
              activeCity={activeCity}
              cities={cities}
              setActiveCity={setActiveCity}
              activeCountry={activeCountry}
            />
          </PageBlockSection>
          <PageBlockSection col2>
            <OfficePageList
              selectedOffice={selectedOffice}
              offices={officesByActiveCountry}
              activeCity={activeCity}
            />
            {!!modalInfo && (
              <OfficePageNote value={modalInfo} />
            )}
          </PageBlockSection>
        </PageBlockSections>
      </PageBlockContent>
    </PageBlock>
  );
};

OfficePageContent.propTypes = {
  offices: PropTypes.array
};

export default OfficePageContent;
